import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { fetchTitilerPointValue } from '../utils/titilerPoint';

/**
 * Nationwide wet-season rice growth stage (Sep 2026).
 * Integer classes: 0 no rice | 1 pre-planting | 2 vegetative | 3 reproductive | 4 ripening | 5 harvested.
 */
export const RICE_GROWTH_STAGE_COG_URL =
  'https://digisaka.s3.amazonaws.com/wet2026_PHILIPPINES_202609.tiff';

/** Philippines extent (EPSG:4326) — same national footprint as planting-method COGs. */
export const RICE_GROWTH_STAGE_BBOX: [number, number, number, number] = [
  116.928, 4.587, 126.604, 20.117,
];

export const RICE_GROWTH_STAGE_LABEL = 'Wet 2026 · Sep';

/** Class 0 is "no rice". It is not painted so the basemap stays visible. */
export const RICE_GROWTH_STAGE_COLORS = [
  '#d9d9d9',
  '#4a90d9',
  '#3cb371',
  '#1f6f3a',
  '#e8a33a',
  '#8c6d62',
] as const;

/**
 * Nationwide class areas for Wet 2026 · Sep (`wet2026_PHILIPPINES_202609.tiff`).
 * `share` is percent of the raster, including class 0.
 */
export const RICE_GROWTH_STAGE_AREA = [
  { stage: 0, name: 'No rice cycle detected', pixels: 25550165, hectares: 2235550.3, share: 74.5 },
  { stage: 1, name: 'Pre-planting / transplanting', pixels: 0, hectares: 0, share: 0 },
  { stage: 2, name: 'Vegetative', pixels: 656477, hectares: 57043.2, share: 1.9 },
  { stage: 3, name: 'Reproductive', pixels: 4567182, hectares: 396919.1, share: 13.2 },
  { stage: 4, name: 'Ripening / maturity', pixels: 2807901, hectares: 244343.1, share: 8.1 },
  { stage: 5, name: 'Harvested / post-harvest', pixels: 738813, hectares: 65117, share: 2.2 },
] as const;

export const RICE_GROWTH_STAGE_LEGEND = {
  colors: [...RICE_GROWTH_STAGE_COLORS],
  labels: [
    'No rice (not drawn)',
    'Pre-planting / transplanting',
    'Vegetative',
    'Reproductive',
    'Ripening / maturity',
    'Harvested',
  ],
  description:
    'Philippines rice crop growth stage for the 2026 wet season (September). Classes 1–5 are drawn; class 0 (no rice) is left transparent.',
};

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** Painted classes only. Class 0 is sent as nodata so it stays transparent. */
const RICE_GROWTH_STAGE_COLORMAP: Record<string, string> = {
  '1': RICE_GROWTH_STAGE_COLORS[1],
  '2': RICE_GROWTH_STAGE_COLORS[2],
  '3': RICE_GROWTH_STAGE_COLORS[3],
  '4': RICE_GROWTH_STAGE_COLORS[4],
  '5': RICE_GROWTH_STAGE_COLORS[5],
};

export async function fetchRiceGrowthStagePoint(
  lon: number,
  lat: number,
  signal?: AbortSignal
): Promise<{ value: number | null; label: string; color: string }> {
  const raw = await fetchTitilerPointValue(RICE_GROWTH_STAGE_COG_URL, lon, lat, { signal });
  const stage = raw == null ? null : Math.round(raw);
  if (stage == null || stage < 0 || stage > 5) {
    return { value: null, label: 'No data', color: '#9ca3af' };
  }
  return {
    value: stage,
    label: RICE_GROWTH_STAGE_LEGEND.labels[stage] ?? 'No data',
    color: RICE_GROWTH_STAGE_COLORS[stage] ?? '#9ca3af',
  };
}

export function buildRiceGrowthStageTileUrl(cogUrl: string = RICE_GROWTH_STAGE_COG_URL): string {
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', '1');
  params.set('colormap', JSON.stringify(RICE_GROWTH_STAGE_COLORMAP));
  params.set('nodata', '0');
  params.set('resampling', 'nearest');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}
